import React, { useState, useEffect } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Landmark, Wallet, Hash } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CURRENCY_FORMATS = {
  AUD: { symbol: '$', locale: 'en-AU' },
  USD: { symbol: '$', locale: 'en-US' },
  IDR: { symbol: 'Rp', locale: 'id-ID' },
  PHP: { symbol: '₱', locale: 'en-PH' },
  THB: { symbol: '฿', locale: 'th-TH' },
  VND: { symbol: '₫', locale: 'vi-VN' },
  MYR: { symbol: 'RM', locale: 'ms-MY' },
};

function formatCurrency(amount, currency) {
  const num = Number(amount) || 0;
  const fmt = CURRENCY_FORMATS[currency];
  if (fmt) {
    try {
      return new Intl.NumberFormat(fmt.locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'IDR' || currency === 'VND' ? 0 : 2,
      }).format(num);
    } catch { /* fall through */ }
  }
  return `${currency} ${num.toLocaleString()}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const BackOfficeFinanceOverview = () => {
  const { isLive } = useEnvironment();
  const env = isLive ? 'live' : 'sandbox';
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLedger = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/backoffice/ledger/summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'X-Environment': env,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSummary(data);
      } catch (err) {
        toast.error('Failed to load ledger summary');
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLedger();
  }, [env]);

  const currencyTotals = summary?.ledger_totals || summary?.currency_totals || summary?.totals_by_currency || {};
  const virtualAccounts = summary?.virtual_accounts || [];
  const totalVAs = summary?.total_vas ?? virtualAccounts.length;
  const pinAggregate = summary?.pin_aggregate;
  const audDrift = summary?.aud_drift;
  const driftOk = summary?.drift_ok;

  if (loading) {
    return (
      <BackOfficeLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="spinner w-8 h-8" />
          <p className="text-sm text-slate-500">Loading ledger overview…</p>
        </div>
      </BackOfficeLayout>
    );
  }

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <Landmark className="w-7 h-7 text-[#0B6445]" />
            Ledger Overview
          </h1>
          <p className="text-[#0E1B16]/70 mt-2 mb-6">
            Aggregate balances across all virtual accounts and currencies.
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(currencyTotals).map(([currency, amount]) => (
            <Card key={currency} className="border-[#DCE4E0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                  <Wallet className="w-4 h-4" /> {currency} Ledger Total
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-[#073B2E]">{formatCurrency(amount, currency)}</p>
              </CardContent>
            </Card>
          ))}
          {pinAggregate && (
            <Card className="border-[#DCE4E0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                  <Landmark className="w-4 h-4" /> PIN Aggregate (AUD)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-[#073B2E]">{formatCurrency(pinAggregate.available, 'AUD')}</p>
                {audDrift != null && (
                  <p className={`text-xs mt-1 font-medium ${driftOk ? 'text-emerald-600' : Math.abs(audDrift) < 100 ? 'text-amber-600' : 'text-red-600'}`}>
                    Drift: {audDrift >= 0 ? '+' : ''}{formatCurrency(audDrift, 'AUD')}
                    {driftOk && ' ✓'}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#0E1B16]/70 flex items-center gap-2">
                <Hash className="w-4 h-4" /> Virtual Accounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#073B2E]">{totalVAs}</p>
            </CardContent>
          </Card>
        </div>

        {/* Virtual Accounts Table */}
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-[#073B2E]">All Virtual Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            {virtualAccounts.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No virtual accounts found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Merchant</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Account Name</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Currency</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Gateway</th>
                      <th className="text-right py-3 px-2 font-semibold text-[#073B2E]">Balance</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">VA / Deposit Ref</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Status</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {virtualAccounts.map((va) => {
                      const vaNumber = va.virtual_account_number || va.internal_va_number || '';
                      const depositRef = va.deposit_reference || '';
                      const gatewayLabel = (va.gateway || '').replace('payitnow', 'PIN').replace('xendit', 'Xendit');
                      return (
                        <tr key={va.id || va._id} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                          <td className="py-3 px-2">
                            <div className="font-medium text-[#0F172A]">{va.merchant_name || va.company_name || '—'}</div>
                            <div className="text-xs text-slate-500">{va.merchant_email || ''}</div>
                            <div className="font-mono text-[10px] text-slate-400 mt-0.5">{(va.merchant_id || '').slice(0, 12)}…</div>
                          </td>
                          <td className="py-3 px-2 text-[#0F172A]">{va.name || '—'}</td>
                          <td className="py-3 px-2">
                            <span className="font-semibold">{va.currency || '—'}</span>
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="text-xs border-slate-200">{gatewayLabel || '—'}</Badge>
                          </td>
                          <td className="py-3 px-2 text-right font-semibold text-[#073B2E]">
                            {formatCurrency(va.balance, va.currency || 'AUD')}
                          </td>
                          <td className="py-3 px-2">
                            {vaNumber && (
                              <div className="font-mono text-xs text-[#0F172A]">{vaNumber}</div>
                            )}
                            {depositRef && (
                              <div className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded inline-block mt-0.5">
                                Ref: {depositRef}
                              </div>
                            )}
                            {va.bank_code && (
                              <div className="text-xs text-slate-500 mt-0.5">Bank: {va.bank_code}</div>
                            )}
                            {!vaNumber && !depositRef && <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-3 px-2">
                            <Badge
                              variant="outline"
                              className={
                                va.status === 'active'
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-slate-200 bg-slate-50 text-slate-600'
                              }
                            >
                              {va.status || 'unknown'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-[#0E1B16]/70 text-xs">{formatDate(va.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeFinanceOverview;
